/**
 * @file Repere au sol du chat du joueur (le chef, catIds[0]) : anneau clair cerne de sombre, plus une copie tres
 * discrete dessinee sans test de profondeur pour rester reperable derriere un arbre ou une maison.
 * Cout : 3 meshes statiques (aucune allocation par frame, aucun shader custom). Aucun mouvement de camera.
 */
import { Group, Mesh, MeshBasicMaterial, RingGeometry } from 'three';
import type { Scene } from 'three';

/** Rayon median de l'anneau (unites monde) : juste au-dela de l'ombre de contact du chat (0.8). */
const RADIUS = 1.02;
const SEGMENTS = 48;
/** Hauteur au-dessus du sol : au-dessus des ombres de contact (0.03), sous les pieds. */
const LIFT = 0.05;

export class HeroMarker {
  private readonly group = new Group();
  private readonly geoms: RingGeometry[] = [];
  private readonly mats: MeshBasicMaterial[] = [];

  constructor(private readonly scene: Scene) {
    this.group.name = 'hero-marker';
    // cerne sombre (contraste sur sol clair : neige, sable)
    this.add(RADIUS - 0.1, RADIUS + 0.13, '#1f1638', 0.42, true, 1);
    // anneau clair
    this.add(RADIUS - 0.05, RADIUS + 0.07, '#fff3c4', 0.92, true, 2);
    // copie « rayons X » : visible a travers les occultants, tres attenuee
    this.add(RADIUS - 0.05, RADIUS + 0.07, '#fff3c4', 0.4, false, 3);
    this.group.visible = false;
    scene.add(this.group);
  }

  private add(inner: number, outer: number, color: string, opacity: number, depthTest: boolean, order: number): void {
    const geo = new RingGeometry(inner, outer, SEGMENTS);
    geo.rotateX(-Math.PI / 2);
    const mat = new MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, depthTest });
    const mesh = new Mesh(geo, mat);
    mesh.renderOrder = depthTest ? order : 20;
    mesh.frustumCulled = false;
    this.geoms.push(geo);
    this.mats.push(mat);
    this.group.add(mesh);
  }

  hide(): void {
    this.group.visible = false;
  }

  /** Place le repere en coordonnees du core (x droite, y vers le fond). */
  show(x: number, y: number): void {
    this.group.visible = true;
    this.group.position.set(x, LIFT, -y);
  }

  dispose(): void {
    this.scene.remove(this.group);
    for (const g of this.geoms) g.dispose();
    for (const m of this.mats) m.dispose();
  }
}
